import { StackContent } from '@core/services/site-content/site-content.types';
import { Component, css, html, LitElement, property } from '@rxdi/lit-html';
import { rich } from '@shared/rich-text';
import { StudioStyles } from '@shared/styles/studio.styles';

/** Home: the full stack we own, as layers. */
@Component({
  selector: 'rs-stack',
  styles: [
    StudioStyles,
    css`
      .layout {
        display: grid;
        gap: clamp(32px, 5vw, 64px);
      }

      @media (min-width: 1080px) {
        .layout {
          grid-template-columns: 1.4fr 1fr;
          align-items: start;
        }
      }

      .layers {
        display: grid;
        gap: 10px;
        perspective: 1200px;
      }

      .layer {
        display: grid;
        grid-template-columns: 1fr;
        gap: 8px;
        padding: 22px 24px;
        border-radius: var(--rs-radius);
        border: 1px solid var(--rs-line);
        background: var(--rs-surface);
        transition: transform 0.4s var(--rs-ease), border-color 0.4s var(--rs-ease), background 0.4s var(--rs-ease);
      }

      @media (min-width: 720px) {
        .layer {
          grid-template-columns: 150px 1fr auto;
          align-items: center;
          gap: 24px;
        }
      }

      .layer:hover {
        transform: translateX(8px);
        border-color: var(--rs-line-strong);
        background: var(--rs-surface-2);
      }

      .layer:nth-child(1) {
        margin-right: 0;
      }
      .layer:nth-child(2) {
        margin-left: 2%;
      }
      .layer:nth-child(3) {
        margin-left: 4%;
      }
      .layer:nth-child(4) {
        margin-left: 6%;
      }
      .layer:nth-child(5) {
        margin-left: 8%;
      }

      @media (max-width: 719px) {
        .layer:nth-child(n) {
          margin-left: 0;
        }
      }

      .name {
        font-family: var(--rs-font-display);
        font-size: 20px;
        font-weight: 600;
        letter-spacing: -0.02em;
      }

      .what {
        color: var(--rs-text-2);
        font-size: 15px;
        line-height: 1.5;
      }

      .ours {
        font-family: var(--rs-font-mono);
        font-size: 12px;
        padding: 6px 12px;
        border-radius: 999px;
        white-space: nowrap;
        width: fit-content;
      }

      .ours.you {
        border: 1px dashed var(--rs-line-strong);
        color: var(--rs-text-2);
      }
      .ours.oss {
        background: var(--rs-ion-soft);
        color: var(--rs-ion);
      }
      .ours.platform {
        background: var(--rs-plasma-soft);
        color: var(--rs-plasma);
      }
      .ours.ops {
        background: var(--rs-signal-soft);
        color: var(--rs-signal);
      }

      .brace {
        display: flex;
        align-items: center;
        gap: 12px;
        margin: 18px 0 0 8%;
        font-family: var(--rs-font-mono);
        font-size: 13px;
        color: var(--rs-signal);
      }

      .brace::before {
        content: '';
        flex: 0 0 48px;
        height: 1px;
        background: var(--rs-signal);
      }

      .promises {
        display: grid;
        gap: 14px;
      }

      .promise {
        padding: 26px;
      }

      .promise h3 {
        margin: 0 0 10px;
        font-family: var(--rs-font-display);
        font-size: 22px;
        letter-spacing: -0.02em;
      }

      .promise p {
        margin: 0;
        color: var(--rs-text-2);
        line-height: 1.6;
      }

      .promise .n {
        display: block;
        margin-bottom: 18px;
        font-family: var(--rs-font-mono);
        font-size: 12px;
        color: var(--rs-text-muted);
      }
    `,
  ],
  template(this: StackComponent) {
    const c = this.content;
    if (!c) {
      return html``;
    }
    return html`
      <section class="section" id="stack">
        <div class="wrap">
          <div class="section-head split">
            <div>
              <span class="kicker"><span class="index">02</span> The stack</span>
              <h2 class="h2">${rich(c.heading)}</h2>
            </div>
            <p class="lead">${rich(c.lead)}</p>
          </div>

          <div class="layout">
            <div>
              <div class="layers">
                ${c.layers.map(
                  (l) => html`
                    <div class="layer">
                      <span class="name">${l.name}</span>
                      <span class="what">${l.what}</span>
                      <span class="ours ${l.owner}">${l.ours}</span>
                    </div>
                  `
                )}
              </div>
              <div class="brace">${c.footnote}</div>
            </div>

            <div class="promises">
              ${c.promises.map(
                (p, i) => html`
                  <div class="card promise">
                    <span class="n">0${i + 1}</span>
                    <h3>${p.title}</h3>
                    <p>${p.body}</p>
                  </div>
                `
              )}
            </div>
          </div>
        </div>
      </section>
    `;
  },
})
export class StackComponent extends LitElement {
  @property({ attribute: false }) content: StackContent;
}
